<template>
    <div class="editable-settings__input-wrapper" @keydown.capture="onWrapperKeyDown">
        <slot name="label" />
        <Input
            ref="inputEl"
            :model-value="modelValue"
            type="number"
            :min="min"
            :max="max"
            class="editable-settings__input"
            size="small"
            :class="{ 'editable-settings__input--with-suffix': showSuffix }"
            :placeholder="placeholder"
            @onChange="onChange"
        >
            <template #append>
                <span v-show="showSuffix">{{ suffix }}</span>
            </template>
        </Input>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
    props: {
        modelValue: {
            type: Number,
            default: undefined
        },
        placeholder: {
            type: String
        },
        suffix: {
            type: String,
            default: "px"
        },
        showSuffix: {
            type: Boolean,
            default: false
        },
        /** For axes where a larger number moves down */
        invertArrows: {
            type: Boolean,
            default: false
        },
        min: {
            type: Number,
            default: undefined
        },
        max: {
            type: Number,
            default: undefined
        }
    },

    emits: ["on-change", "wrapperKeyDown", "update:modelValue"],

    methods: {
        clamp(value: number) {
            const min = this.min ?? -Infinity;
            const max = this.max ?? Infinity;

            return Math.min(Math.max(value, min), max);
        },

        /**
         * The field renders from the Input's own copy of the value, and every later render puts
         * that copy back. Writing the element alone is undone a moment later, and emitting is not
         * enough either: a value that clamps back to what we already stored is not a prop change.
         */
        resyncInput(input: HTMLInputElement, value: number) {
            input.value = String(value);
            (this.$refs.inputEl as { setCurrentValue?: (next: number) => void } | undefined)?.setCurrentValue?.(value);
        },

        onChange(event: InputEvent) {
            const input = event.target as HTMLInputElement;
            const value = input.value;

            if (value === "" || isNaN(Number(value))) {
                this.$emit("update:modelValue", undefined);
                return;
            }

            const clamped = this.clamp(Number(value));

            if (clamped !== Number(value)) {
                this.resyncInput(input, clamped);
            }

            this.$emit("update:modelValue", clamped);
        },

        getMoveByValue(event: KeyboardEvent, step = 1) {
            let moveBy = event.key === "ArrowLeft" || event.key === "ArrowUp" ? step : -step;

            if (event.shiftKey) {
                moveBy *= 10;
            }

            return moveBy;
        },

        onWrapperKeyDown(event: KeyboardEvent) {
            if (!["ArrowUp", "ArrowDown"].includes(event.key)) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();

            const moveBy = this.getMoveByValue(event) * (this.invertArrows ? -1 : 1);
            const placeholderValue = this.placeholder ? parseFloat(this.placeholder) : 0;
            const fallback = Number.isNaN(placeholderValue) ? 0 : placeholderValue;
            // ?? not ||: a stored 0 must step from 0
            const currentValue = this.modelValue ?? fallback;

            this.$emit("update:modelValue", this.clamp(currentValue + moveBy));
        }
    }
});
</script>
